<template>
  <div class="log-page">
    <div class="background-header"></div>
    <div class="shell">
      <header class="log-topbar">
        <button class="back-btn" type="button" @click="$router.go(-1)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 19L8 12L15 5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <span class="log-topbar-title">{{ t.balanceDetail }}</span>
        <span class="log-topbar-spacer"></span>
      </header>

      <div class="log-list" v-if="logs.length > 0">
        <div class="log-item" v-for="(item, i) in logs" :key="i">
          <div class="log-info">
            <div class="log-type">{{ getTypeName(item.log_type) }}</div>
            <div class="log-date">{{ formatDate(item.created_at) }}</div>
          </div>
          <div class="log-amount" :class="{ 'log-amount--positive': item.amount > 0 }">
            {{ item.amount > 0 ? '+' : '' }}{{ item.amount }} OC
          </div>
        </div>
      </div>

      <div class="log-empty" v-if="logs.length === 0 && !loading">{{ t.noRecords }}</div>

      <div class="load-more" v-if="hasMore">
        <button class="load-more-btn" type="button" :disabled="loading" @click="loadMore">
          {{ loading ? '...' : t.loadMore }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getBalanceLogs } from '@/api/auth'
import { getMessages } from '@/i18n'

export default {
  name: 'BalanceLogPage',
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      logs: [],
      page: 1,
      totalPages: 1,
      loading: false,
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
    hasMore() {
      return this.page < this.totalPages
    },
  },
  watch: {
    '$route'() {
      this.lang = localStorage.getItem('lang') || 'en'
    },
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    this.loadLogs(1)
  },
  methods: {
    async loadLogs(page) {
      if (this.loading) return
      this.loading = true
      try {
        const res = await getBalanceLogs(page)
        const data = res.data
        this.totalPages = data.total_pages || 1
        if (page === 1) {
          this.logs = data.logs || []
        } else {
          this.logs = this.logs.concat(data.logs || [])
        }
        this.page = data.current_page || page
      } catch (e) {
        console.error('loadBalanceLogs failed', e)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.loadLogs(this.page + 1)
    },
    getTypeName(type) {
      const map = {
        order_income: this.t.typeOrderIncome,
        reward: this.t.typeReward,
        generation_reward: this.t.typeGenerationReward,
        withdraw: this.t.typeWithdraw,
        withdraw_refund: this.t.typeWithdrawRefund,
        reinvest: this.t.typeReinvest,
        redeem: this.t.typeRedeem,
      }
      return map[type] || type
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    getRemark(item) {
      if (item.remark) return item.remark
      const labels = {
        order_income: this.t.typeOrderIncome,
        reward: this.t.typeReward,
        withdraw: this.t.typeWithdraw,
        withdraw_refund: this.t.typeWithdrawRefund,
        reinvest: this.t.typeReinvest,
        redeem: this.t.typeRedeem,
      }
      return labels[item.log_type] || item.log_type
    },
  },
}
</script>

<style lang="less" scoped>
.log-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: relative;
}

.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
}

.log-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.back-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.log-topbar-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.log-topbar-spacer {
  width: 36px;
}

.log-list {
  margin-top: 8px;
}

.log-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px;
  border-radius: 10px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.log-info {
  min-width: 0;
  flex: 1;
}

.log-type {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.log-date {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
}

.log-amount {
  font-size: 15px;
  font-weight: 600;
  color: #ff5577;
  flex-shrink: 0;
  margin-left: 8px;

  &--positive {
    color: #14d990;
  }
}

.log-empty {
  text-align: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  padding: 60px 0;
}

.load-more {
  text-align: center;
  margin-top: 16px;
}

.load-more-btn {
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  padding: 8px 24px;
  border-radius: 20px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
